import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import {
  Headphones,
  Play,
  Pause,
  ExternalLink,
  BookOpen,
  GraduationCap,
  ChevronRight,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import BrandNav from "../components/BrandNav";
import ImageLightbox from "../components/ImageLightbox";

// Voice-slug → human label. Only relevant during Milestone-2 previews.
const VOICE_LABEL = {
  grace_bishop:   "Grace Bishop \u00b7 warm, natural",
  aubrey_ellis:   "Aubrey Ellis \u00b7 confident storyteller",
  rowan_parker:   "Rowan Parker \u00b7 young narrator",
  david_anderson: "David Anderson \u00b7 hushed and gentle",
  james_coleman:  "James Coleman \u00b7 documentary calm",
};

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function Meet() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialVariant = searchParams.get("variant") || null;

  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [variant, setVariant] = useState(initialVariant);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  // Storybook mode — populated only for heroes that have a scenes manifest.
  const [scenes, setScenes] = useState(null);
  const [activeSceneIdx, setActiveSceneIdx] = useState(0);
  const audioRef = useRef(null);
  const scrubberRef = useRef(null);

  // Lightbox state — one shared instance used by both the static portrait
  // panel and the storybook scene stage. `sceneIdx = null` means the
  // portrait is being shown; a number means we're viewing that scene index.
  const [lightbox, setLightbox] = useState({ open: false, mode: null, sceneIdx: 0 });
  const portraitTriggerRef = useRef(null);
  const sceneTriggerRef = useRef(null);

  // Fetch metadata whenever slug changes.
  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, error: null });
    setScenes(null);
    setActiveSceneIdx(0);
    fetch(`${API_BASE}/api/portraits/${encodeURIComponent(slug)}/metadata`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setState({ loading: false, data: null, error: body.detail || "This portrait audio couldn't be loaded." });
          return;
        }
        setState({ loading: false, data: body, error: null });

        // Storybook mode — best-effort fetch of the scenes manifest.
        // Absence is fine; player gracefully falls back to audio-only.
        if (body && body.has_scenes) {
          fetch(`${API_BASE}/api/portraits/${encodeURIComponent(slug)}/scenes`)
            .then((r) => r.ok ? r.json() : null)
            .then((m) => { if (alive && m && Array.isArray(m.scenes)) setScenes(m); })
            .catch(() => { /* audio-only fallback */ });
        }

        // Do NOT auto-select a variant when a canonical mp3 exists — that
        // would silently swap the user's chosen production voice (Grace Bishop
        // for Ada, David Anderson for Turing) for whichever variant sorts
        // alphabetically first. Only pick a variant when the caller explicitly
        // asked for one via ?variant= in the URL.
        // The variant switcher remains visible below for A/B previews.

        // Analytics: portrait viewed.
        fetch(`${API_BASE}/api/marketing/event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "portrait_page_view",
            portrait_slug: slug,
            referrer: typeof document !== "undefined" ? document.referrer || "" : "",
          }),
          keepalive: true,
        }).catch(() => { /* swallow */ });
      })
      .catch(() => {
        if (alive) setState({ loading: false, data: null, error: "Network error while loading this page." });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Build the audio URL from the chosen variant + slug.
  const audioUrl = useMemo(() => {
    if (!state.data) return null;
    const base = `${API_BASE}/api/portraits/${encodeURIComponent(slug)}/audio`;
    return variant ? `${base}?variant=${encodeURIComponent(variant)}` : base;
  }, [slug, variant, state.data]);

  // Pause/reset when variant changes so we always play the newly-chosen voice.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
    // Push variant into the URL as ?variant= so the page is shareable.
    if (variant) {
      const next = new URLSearchParams(searchParams);
      next.set("variant", variant);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => toast.error("Could not start playback."));
      // Analytics: fire once per play button press.
      fetch(`${API_BASE}/api/marketing/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "portrait_audio_played",
          portrait_slug: slug,
          variant: variant || null,
        }),
        keepalive: true,
      }).catch(() => { /* swallow */ });
    } else {
      el.pause();
    }
  };

  const onScrub = (e) => {
    const el = audioRef.current;
    if (!el || !progress.total) return;
    const rect = scrubberRef.current.getBoundingClientRect();
    const rel = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = rel * progress.total;
  };

  // Advance the active scene index whenever the audio position crosses a
  // scene boundary. Uses the manifest's `start_seconds` / `end_seconds`.
  useEffect(() => {
    if (!scenes || !Array.isArray(scenes.scenes) || scenes.scenes.length === 0) return;
    const t = progress.current;
    const idx = scenes.scenes.findIndex(
      (s) => t >= s.start_seconds && t < s.end_seconds
    );
    // If time exceeds the last scene's end, stick on the final scene.
    if (idx === -1) {
      const last = scenes.scenes.length - 1;
      if (t >= scenes.scenes[last].end_seconds) setActiveSceneIdx(last);
      return;
    }
    if (idx !== activeSceneIdx) setActiveSceneIdx(idx);
  }, [progress.current, scenes, activeSceneIdx]);

  const activeScene = scenes && scenes.scenes ? scenes.scenes[activeSceneIdx] : null;
  const audioEnded = progress.total > 0 && progress.current >= progress.total - 0.25;

  const restart = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().catch(() => {});
  };

  const copyShareLink = async () => {
    try {
      const url = typeof window !== "undefined" ? window.location.href : "";
      await navigator.clipboard.writeText(url);
      toast.success("Link copied \u2014 share this page with anyone.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  return (
    <div data-testid="meet-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <div className="ftg-meet">
          {state.loading && (
            <div className="ftg-loader" data-testid="meet-loading">
              <div className="ftg-loader__dot" />
            </div>
          )}

          {!state.loading && state.error && (
            <div className="ftg-verify__card ftg-verify__card--invalid" data-testid="meet-invalid">
              <ShieldAlert size={36} />
              <h1 className="ftg-verify__title">Portrait not found</h1>
              <p className="ftg-verify__body">{state.error}</p>
              <Link to="/" className="ftg-btn ftg-btn--ghost" data-testid="meet-home">Back to home</Link>
            </div>
          )}

          {!state.loading && state.data && (
            <>
              <article className="ftg-meet__card" data-testid="meet-card">
                <div className="ftg-meet__eyebrow">
                  <Sparkles size={14} /> Meet Someone Amazing
                </div>
                <h1 className="ftg-meet__title" data-testid="meet-title">
                  {state.data.name}
                </h1>
                <p className="ftg-meet__lede">
                  A two-minute audio drama for young STEAM learners &mdash; warm narration,
                  gentle music, and three true stories to spark curiosity.
                </p>

                {/* Static portrait panel — for audio-only heroes (no scenes
                    manifest). The 4 storybook heroes render `activeScene`
                    above; everyone else with a portrait file gets this
                    warm-framed static image instead of a blank card. */}
                {!activeScene && state.data.portrait_url && (
                  <div className="ftg-portrait" data-testid="portrait-panel">
                    <button
                      type="button"
                      ref={portraitTriggerRef}
                      className="ftg-portrait__stage ftg-portrait__stage--btn"
                      aria-label={`Enlarge portrait of ${state.data.name}`}
                      onClick={() => setLightbox({ open: true, mode: "portrait", sceneIdx: 0 })}
                      data-testid="portrait-open-lightbox"
                    >
                      <img
                        src={`${API_BASE}${state.data.portrait_url}`}
                        alt={`Portrait of ${state.data.name}`}
                        loading="eager"
                        className="ftg-portrait__img"
                        data-testid="portrait-image"
                      />
                      <div className="ftg-portrait__pip" aria-hidden="true">
                        Portrait &middot; click to enlarge
                      </div>
                    </button>
                    <p className="ftg-portrait__caption" data-testid="portrait-caption">
                      {state.data.name}
                      <span className="ftg-portrait__caption-thin">
                        {" \u2014 a short audio drama"}
                        {state.data.duration_seconds
                          ? ` \u00b7 ${formatTime(state.data.duration_seconds)}`
                          : ""}
                      </span>
                    </p>
                  </div>
                )}

                {/* Storybook panel — 5 hand-painted scenes cross-fading in
                    time with the narration. Only rendered when a scenes
                    manifest exists for this hero; otherwise the player
                    stays audio-only. */}
                {activeScene && (
                  <div className="ftg-storybook" data-testid="storybook-panel">
                    <div
                      ref={sceneTriggerRef}
                      className="ftg-storybook__stage ftg-storybook__stage--btn"
                      role="button"
                      tabIndex={0}
                      aria-label={`Enlarge scene ${activeSceneIdx + 1}: ${activeScene.alt}`}
                      onClick={() => setLightbox({ open: true, mode: "scene", sceneIdx: activeSceneIdx })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setLightbox({ open: true, mode: "scene", sceneIdx: activeSceneIdx });
                        }
                      }}
                      data-testid="storybook-open-lightbox"
                    >
                      {scenes.scenes.map((s, i) => {
                        const isActive = i === activeSceneIdx;
                        const m = s.motion || null;
                        const durationS = Math.max(4, s.end_seconds - s.start_seconds);
                        // Compose per-motion inline CSS variables the
                        // stylesheet consumes. Fresh animation on every
                        // become-active by keying on isActive so React
                        // re-mounts the element and restarts the CSS
                        // animation from frame 0.
                        const style = isActive && m ? {
                          "--kb-duration": `${durationS}s`,
                          "--kb-origin": m.origin || "center center",
                          "--kb-from-scale": String(m.from_scale ?? 1.0),
                          "--kb-to-scale": String(m.to_scale ?? (m.scale ?? 1.06)),
                          "--kb-from-x": `${m.from_translate_pct ?? 0}%`,
                          "--kb-to-x": `${m.to_translate_pct ?? 0}%`,
                          "--kb-from-y": `${m.from_translate_pct ?? 0}%`,
                          "--kb-to-y": `${m.to_translate_pct ?? 0}%`,
                        } : undefined;
                        const motionClass = isActive && m
                          ? `ftg-kb ftg-kb--${m.type}`
                          : "";
                        return (
                          <img
                            // key change on activation forces animation restart
                            key={`${s.index}-${isActive ? "on" : "off"}`}
                            src={`${API_BASE}${s.image_url}`}
                            alt={s.alt}
                            loading={i === 0 ? "eager" : "lazy"}
                            className={`ftg-storybook__img ${isActive ? "is-active" : ""} ${motionClass}`}
                            style={style}
                            data-testid={`storybook-scene-${s.index}`}
                            data-active={isActive ? "true" : "false"}
                            data-motion={m ? m.type : "none"}
                          />
                        );
                      })}
                      {audioEnded && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); restart(); }}
                          className="ftg-storybook__replay"
                          data-testid="storybook-replay"
                        >
                          <Play size={14} /> Play again
                        </button>
                      )}
                      <div className="ftg-storybook__pip" aria-hidden="true">
                        Scene {activeSceneIdx + 1} of {scenes.scenes.length} &middot; click to enlarge
                      </div>
                    </div>
                    <p
                      className="ftg-storybook__subtitle"
                      data-testid="storybook-subtitle"
                      key={activeScene.index}
                    >
                      {activeScene.narration_text}
                    </p>
                  </div>
                )}

                {/* voice-variant switcher (Milestone-2 only shows when >1 variant) */}
                {Array.isArray(state.data.variants) && state.data.variants.length > 1 && (
                  <div className="ftg-meet__variants" data-testid="meet-variants">
                    <div className="ftg-meet__variants-label">Compare narrator voices:</div>
                    <div className="ftg-meet__variant-row">
                      {state.data.variants.map((v) => (
                        <button
                          key={v}
                          type="button"
                          className={`ftg-btn ftg-btn--sm ${v === variant ? "ftg-btn--secondary" : "ftg-btn--ghost"}`}
                          onClick={() => setVariant(v)}
                          data-testid={`meet-variant-${v}`}
                        >
                          {VOICE_LABEL[v] || v}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Player */}
                <div className="ftg-meet__player" data-testid="meet-player">
                  <button
                    type="button"
                    onClick={togglePlay}
                    className={`ftg-meet__play ${playing ? "is-playing" : ""}`}
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="meet-play-button"
                  >
                    {playing ? <Pause size={22} /> : <Play size={22} />}
                  </button>
                  <div className="ftg-meet__player-body">
                    <div className="ftg-meet__player-title">
                      {playing ? "Playing" : "Listen"}
                      <span className="ftg-meet__player-sub">{state.data.name}</span>
                    </div>
                    <div
                      ref={scrubberRef}
                      className="ftg-meet__scrubber"
                      onClick={onScrub}
                      role="slider"
                      aria-valuemin={0}
                      aria-valuemax={progress.total || 0}
                      aria-valuenow={progress.current || 0}
                      tabIndex={0}
                      data-testid="meet-scrubber"
                    >
                      <div
                        className="ftg-meet__scrubber-fill"
                        style={{ width: `${progress.total ? (100 * progress.current / progress.total) : 0}%` }}
                      />
                    </div>
                    <div className="ftg-meet__player-times">
                      <span>{formatTime(progress.current)}</span>
                      <span>{formatTime(progress.total || state.data.duration_seconds || 0)}</span>
                    </div>
                  </div>
                </div>

                {audioUrl && (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    preload="metadata"
                    onLoadedMetadata={(e) => setProgress({ current: 0, total: e.target.duration || 0 })}
                    onTimeUpdate={(e) => setProgress((p) => ({ ...p, current: e.target.currentTime }))}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                    data-testid="meet-audio-element"
                  />
                )}

                <div className="ftg-meet__share-row">
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                    data-testid="meet-share-copy"
                  >
                    Copy share link
                  </button>
                  {state.data.script_available && (
                    <a
                      href={`${API_BASE}/api/portraits/${slug}/script`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                      data-testid="meet-view-script"
                    >
                      View script
                    </a>
                  )}
                </div>
              </article>

              <aside className="ftg-cta-card" data-testid="meet-cta">
                <div className="ftg-cta-card__eyebrow">Explore FutureTec Global</div>
                <h2 className="ftg-cta-card__title">
                  Curious minds start here.
                  <span className="ftg-cta-card__title-thin"> Give your child 300 audio lessons and 75 chapter books.</span>
                </h2>
                <ul className="ftg-cta-card__bullets">
                  <li><Headphones size={16} /> Warm audio lessons for ages 6&ndash;17.</li>
                  <li><BookOpen size={16} /> 75 printable Deep-Dive Reader chapter books.</li>
                  <li><GraduationCap size={16} /> Real certificates verified globally.</li>
                </ul>
                <div className="ftg-cta-card__actions">
                  <Link to="/" className="ftg-btn ftg-btn--secondary" data-testid="meet-cta-start">
                    Start free preview <ChevronRight size={14} />
                  </Link>
                  <Link to="/#pricing" className="ftg-btn ftg-btn--ghost" data-testid="meet-cta-pricing">
                    <ExternalLink size={14} /> See pricing
                  </Link>
                </div>
              </aside>
            </>
          )}
        </div>
      </main>

      {/* Fullscreen image lightbox — shared between the static portrait
          panel (audio-only heroes) and the storybook scene stage. Scene
          mode enables prev/next arrows to flip through all 5 scenes for
          classroom projection. */}
      {state.data && (
        <ImageLightbox
          open={lightbox.open}
          onClose={() => setLightbox((l) => ({ ...l, open: false }))}
          returnFocusRef={lightbox.mode === "scene" ? sceneTriggerRef : portraitTriggerRef}
          src={
            lightbox.mode === "scene" && scenes && scenes.scenes && scenes.scenes[lightbox.sceneIdx]
              ? `${API_BASE}${scenes.scenes[lightbox.sceneIdx].image_url}`
              : (state.data.portrait_url ? `${API_BASE}${state.data.portrait_url}` : "")
          }
          alt={
            lightbox.mode === "scene" && scenes && scenes.scenes && scenes.scenes[lightbox.sceneIdx]
              ? scenes.scenes[lightbox.sceneIdx].alt
              : `Portrait of ${state.data.name}`
          }
          caption={
            lightbox.mode === "scene" && scenes && scenes.scenes && scenes.scenes[lightbox.sceneIdx]
              ? `Scene ${lightbox.sceneIdx + 1} of ${scenes.scenes.length} \u2014 ${state.data.name}`
              : `${state.data.name} \u2014 Portrait`
          }
          onPrev={lightbox.mode === "scene"
            ? () => setLightbox((l) => ({ ...l, sceneIdx: Math.max(0, l.sceneIdx - 1) }))
            : undefined}
          onNext={lightbox.mode === "scene" && scenes && scenes.scenes
            ? () => setLightbox((l) => ({ ...l, sceneIdx: Math.min(scenes.scenes.length - 1, l.sceneIdx + 1) }))
            : undefined}
          hasPrev={lightbox.mode === "scene" && lightbox.sceneIdx > 0}
          hasNext={lightbox.mode === "scene" && scenes && scenes.scenes && lightbox.sceneIdx < scenes.scenes.length - 1}
        />
      )}
    </div>
  );
}
